/**
 * H-47 模态键盘边界：焦点陷阱 + Esc + 焦点归还 + dialog 语义
 *
 * 三件事各自独立，缺的都是「键盘用户」这条腿：
 *   1. 关闭态弹窗只剩 opacity:0 + pointer-events:none —— pointer-events 管不了 Tab 和 Enter，
 *      隐形弹窗里的按钮照样能被聚焦、被回车激活（退圈/解约这类会删存档的就在其中）。
 *      这一条由 ui.css 的 `visibility: hidden` 关掉（见 H-47① 注释），本模块不负责。
 *   2. 弹窗打开后 Tab 会走到背景层的控件上（焦点陷阱缺失）。
 *   3. 弹窗没有 Esc，关闭后焦点也不知道该回哪儿（键盘用户每次都要从头 Tab 一遍）。
 *
 * 接线方式：不在 8 处 open/close 调用点逐个挂，改为观察 `.show` 这个 class 的翻转
 * （全站弹窗的显隐都只经由它），面板无需感知本模块存在。
 *
 * 三条刻意的取舍：
 *   - 【不拦 Esc 的弹窗就不该被拦】月度执行主弹窗与竞角揭晓层的「返回/继续执行」是推进结算，
 *     不是取消，所以它们既无 `.btn-cancel` 也无 data-esc，Esc 落到它们身上是空操作。
 *   - 【没有可聚焦内容的层不入栈】纯遮罩 `.dimoverlay` 只是背景，压住它会误拦下一层的键盘行为。
 *   - 【`.vn-dialog` 也归本模块管】当面对话浮层展开时用淡底真拦页面点击（第十四批改判，报告 §8.20），
 *     那就必须同时是键盘层，否则鼠标点不到而 Tab 走得进去。它同样既无 `.btn-cancel` 也无
 *     `data-esc` ⇒ Esc 是空操作，正对上用户「无跳过、无页码」的永久裁定。
 */

/** 全站需要键盘边界的层：卡片弹窗容器 + 遮罩层 + 当面对话浮层 */
const MODAL_SELECTOR = '.modal-wrap, .dimoverlay, .vn-dialog';

/** 可被 Tab 停到的元素（role=button 的行内块靠 [tabindex] 命中，见 H-40 的 makeClickable） */
const FOCUSABLE_SELECTOR = [
    'a[href]', 'button:not([disabled])', 'input:not([disabled])',
    'select:not([disabled])', 'textarea:not([disabled])', '[tabindex]:not([tabindex="-1"])',
].join(',');

/** 从新到旧的处理中弹窗栈；栈顶 = 当前键盘层 */
const stack: HTMLElement[] = [];
/** 每个弹窗打开前的焦点所在，关闭时归还 */
const returnFocus = new WeakMap<HTMLElement, HTMLElement | null>();

function alive(scope: HTMLElement): boolean {
    // getClientRects() 为空 = 祖先 display:none（面板被 hidden-panel 隐藏时弹窗还没收 .show）
    return scope.classList.contains('show') && scope.getClientRects().length > 0;
}

function focusables(scope: HTMLElement): HTMLElement[] {
    return Array.from(scope.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
        .filter((el) => el.getClientRects().length > 0);
}

/** Esc 的关闭动作复用各弹窗自己的关闭路径，不另写一套状态机 */
function dismiss(scope: HTMLElement): void {
    const cancel = scope.querySelector<HTMLElement>('.btn-cancel');
    if (cancel && cancel.getClientRects().length) {
        cancel.click();
        return;
    }
    // 只读详情层：点卡片外的透明遮罩关闭，与程序化 click 自身的命中判定一致（e.target === 根）
    if (scope.dataset.esc === 'self') scope.click();
}

function enter(scope: HTMLElement): void {
    const prev = document.activeElement;
    returnFocus.set(scope, prev instanceof HTMLElement && prev !== document.body ? prev : null);
    stack.push(scope);

    scope.setAttribute('role', 'dialog');
    scope.setAttribute('aria-modal', 'true');
    // 可及名称：默认取卡内标题 .mtitle；卡里没有 .mtitle 的面板（网友评价用的是自己的 .review-title，
    // 剧情卡压根没有标题行）改由 `data-dialog-label` 声明——只喂名字，不动版式（2026-10-16 弹窗统一批）。
    const name = scope.dataset.dialogLabel || scope.querySelector('.mtitle')?.textContent;
    if (name) scope.setAttribute('aria-label', name);

    const first = focusables(scope)[0];
    // 焦点已在弹窗内（如弹窗内容重建时重新加 .show）就不要抢回去
    if (first && !scope.contains(document.activeElement)) first.focus();
}

function exit(scope: HTMLElement, opts: { restore?: boolean } = {}): void {
    const i = stack.indexOf(scope);
    if (i < 0) return;
    stack.splice(i, 1);

    scope.removeAttribute('role');
    scope.removeAttribute('aria-modal');
    scope.removeAttribute('aria-label');

    if (opts.restore === false) return;
    const back = returnFocus.get(scope);
    returnFocus.delete(scope);
    // 触发元素可能已随面板隐藏，focus() 在隐形元素上是空操作，不会抛错
    if (back && back.getClientRects().length) back.focus();
}

function prune(): void {
    while (stack.length && !alive(stack[stack.length - 1])) {
        // 面板整块被隐藏（如弹窗开着就切了页）：焦点已随 display:none 丢掉，不必归还
        exit(stack[stack.length - 1], { restore: false });
    }
}

function onKeydown(e: KeyboardEvent): void {
    prune();
    const top = stack[stack.length - 1];
    if (!top) return;

    if (e.key === 'Escape') {
        // 输入法候选框里的 Esc 不该关弹窗
        if (e.isComposing || e.keyCode === 229) return;
        const before = top;
        dismiss(top);
        // 该层不吃 Esc（结算类弹窗）时不吞事件，留给可能的其他监听者
        if (stack[stack.length - 1] !== before || !alive(before)) e.preventDefault();
        return;
    }
    if (e.key !== 'Tab') return;

    const items = focusables(top);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    const current = document.activeElement as HTMLElement | null;
    const inside = !!current && top.contains(current);

    e.preventDefault();
    if (!inside) {
        (e.shiftKey ? last : first).focus();
    } else if (e.shiftKey) {
        (items[items.indexOf(current!) - 1] ?? last).focus();
    } else {
        (items[items.indexOf(current!) + 1] ?? first).focus();
    }
}

/** 观察 `.show` 翻转。由 app.ts 在挂载外壳后调用一次。 */
export function initModalKeyboard(root: HTMLElement): void {
    const observed = (node: HTMLElement) => node.classList && node.matches(MODAL_SELECTOR);

    new MutationObserver((records) => {
        for (const rec of records) {
            const el = rec.target as HTMLElement;
            if (!observed(el)) continue;
            if (alive(el)) {
                // 纯遮罩 .dimoverlay 只是背景，没有可聚焦内容：入栈反而会误拦下一层的键盘行为
                if (!stack.includes(el) && focusables(el).length) enter(el);
            } else {
                exit(el);
            }
        }
        // 同一批里若有层被关掉，栈顶可能已经换人：让下一次按键看到干净的栈
        prune();
    }).observe(root, {
        subtree: true,
        attributes: true,
        attributeFilter: ['class'],
    });

    document.addEventListener('keydown', onKeydown, true);
}
